import { Button, Form, FormProps, Modal, ModalProps, Space, Steps } from 'antd';
import { HttpError, useList } from '@refinedev/core';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { ModalCreateAssignmentStep1 } from './ModalCreateAssignmentStep1';
import { ModalCreateAssignmentStep2 } from './ModalCreateAssignmentStep2';
import { ModalCreateAssignmentStep3 } from './ModalCreateAssignmentStep3';
import { handleWizardError } from 'utils/error-handler';
import { useCallback, useMemo, useState } from 'react';
import type { IProfessorAreaEvaluationAssignmentCreate } from 'interfaces/professor_area/assignments';
import type { IProfessorAreaEvaluationsList } from 'interfaces/professor_area/evaluations';

interface ModalCreateAssignmentProps {
  modalProps: ModalProps;
  formProps: FormProps<IProfessorAreaEvaluationAssignmentCreate>;
  classroomId: string;
  isLoading: boolean;
  onCloseModal: () => void;
}

export const ModalCreateAssignment = ({
  formProps,
  modalProps,
  classroomId,
  isLoading,
  onCloseModal
}: ModalCreateAssignmentProps) => {
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm();
  const [type, setType] = useState<'list' | 'new'>('list');

  const evaluationsResource = 'professor_area/classroom_assignments';
  const evaluationsMeta = { classroomId };
  const modalIsOpen = Boolean(modalProps.open ?? modalProps.visible);
  const fetchEnabled = Boolean(classroomId && modalIsOpen);

  const { data: assessmentsResult, isLoading: assessmentsLoading } = useList<
    IProfessorAreaEvaluationsList,
    HttpError
  >({
    resource: evaluationsResource,
    meta: evaluationsMeta,
    pagination: { pageSize: 200 },
    filters: [
      {
        field: 'filter[by_evaluation_type]',
        operator: 'eq',
        value: 'Assessment'
      }
    ],
    queryOptions: { enabled: fetchEnabled }
  });

  const { data: assignmentsResult, isLoading: assignmentsLoading } = useList<
    IProfessorAreaEvaluationsList,
    HttpError
  >({
    resource: evaluationsResource,
    meta: evaluationsMeta,
    pagination: { pageSize: 200 },
    filters: [
      {
        field: 'filter[by_evaluation_type]',
        operator: 'eq',
        value: 'Assignment'
      }
    ],
    queryOptions: { enabled: fetchEnabled }
  });

  const classroomEvaluations = useMemo(
    () => [
      ...(assessmentsResult?.data ?? []),
      ...(assignmentsResult?.data ?? [])
    ],
    [assessmentsResult?.data, assignmentsResult?.data]
  );

  const evaluationsLoading = assessmentsLoading || assignmentsLoading;

  const steps = useMemo(
    () => [
      {
        title: 'Informações básicas',
        content: <ModalCreateAssignmentStep1 />
      },
      ...(type === 'new'
        ? [
            {
              title: 'Instruções e critérios',
              content: <ModalCreateAssignmentStep2 />
            }
          ]
        : []),
      {
        title: 'Parâmetros',
        content: (
          <ModalCreateAssignmentStep3
            classroomEvaluations={classroomEvaluations}
            evaluationsLoading={evaluationsLoading}
          />
        )
      }
    ],
    [type, classroomEvaluations, evaluationsLoading]
  );
  const [formData, setFormData] =
    useState<IProfessorAreaEvaluationAssignmentCreate>(
      {} as IProfessorAreaEvaluationAssignmentCreate
    );

  const onFinish = useCallback(async () => {
    try {
      await form.validateFields();
    } catch (error: unknown) {
      handleWizardError(form, error);
      return;
    }

    const dataForm = { ...formData, ...form.getFieldsValue() } as Record<
      string,
      unknown
    >;
    // optional_evaluation is FE-only sugar (drives weight 0); never sent.
    const { optional_evaluation: _optionalEvaluation, ...rest } = dataForm;

    const finalData: IProfessorAreaEvaluationAssignmentCreate = {
      ...(rest as IProfessorAreaEvaluationAssignmentCreate),
      evaluation_type: 'Assignment',
      classroom_id: classroomId
    };

    if (finalData.kind === 'final') {
      const data = finalData as unknown as Record<string, unknown>;
      delete data.weight;
      delete data.parent_id;
      delete data.rule;
    }

    try {
      await formProps?.onFinish?.(finalData);
      onCloseModal();
    } catch {
      // Reported by refine's errorNotification; keep the modal open so the
      // user can correct and retry.
    }
  }, [form, formData, classroomId, formProps, onCloseModal]);

  const changeStep = async (step: number, validate?: boolean) => {
    try {
      if (validate) {
        await form.validateFields();
      }

      const currentValues = form.getFieldsValue();

      setFormData(prev => ({ ...prev, ...currentValues }));
      setCurrent(step);
    } catch (error: unknown) {
      handleWizardError(form, error);
    }
  };

  return (
    <Modal
      {...modalProps}
      title="Adicionar atividade à turma"
      width={900}
      style={{ top: 20, paddingBottom: 20 }}
      centered
      maskClosable={false}
      afterClose={() => {
        form.resetFields();
        setFormData({} as IProfessorAreaEvaluationAssignmentCreate);
        setType('list');
        setCurrent(0);
      }}
      footer={
        <Space className="justify-end w-full">
          {current > 0 && (
            <Button
              icon={<LeftOutlined />}
              onClick={() => changeStep(current - 1)}
            >
              Voltar
            </Button>
          )}

          {current < steps.length - 1 && (
            <Button
              type="primary"
              icon={<RightOutlined />}
              iconPosition="end"
              onClick={() => changeStep(current + 1, true)}
            >
              Próximo
            </Button>
          )}

          {current === steps.length - 1 && (
            <Button
              htmlType="submit"
              type="primary"
              loading={isLoading}
              onClick={onFinish}
            >
              Criar atividade
            </Button>
          )}
        </Space>
      }
    >
      <div className="grid grid-cols-[240px_1fr] divide-x">
        <Steps
          current={current}
          items={steps}
          className="h-fit overflow-y-auto [&_.ant-steps-item-finish_.ant-steps-item-icon]:bg-primary-500/10"
          direction="vertical"
        />
        <Form
          form={form}
          layout="vertical"
          initialValues={formData}
          className="pl-6"
          onValuesChange={changedValues => {
            if (changedValues.type) {
              setType(changedValues.type);
            }
          }}
        >
          {steps[current].content}
        </Form>
      </div>
    </Modal>
  );
};
